import React, { Dispatch, SetStateAction, useState } from 'react';

// Mantine
import { Flex, Group } from '@mantine/core';
import { useNotification } from '@refinedev/core';

import { useHotkeys } from '@mantine/hooks';
import { MRT_TableInstance } from 'mantine-react-table';

// Icons
import { IconFilePlus } from '@tabler/icons-react';

// Interfaces
import {
  Claim,
  CreateSubmission,
  Payor,
  Provider,
  Submission,
  SubmissionType,
} from 'interfaces';

// Store
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

// Components
import { InternalUploadDataButton, TableActionButton } from 'components/common';
import {
  hasCompleteSelectedDateRange,
  hasEmptySelectedDateRange,
  formatSelectedDateRange,
} from 'utils/submissionCriteria';

import {
  ClaimPatientPaymentHistoryModal,
  PatientHistoryModal,
  GenerateBlueCrossButton,
  ClaimCreateComboButton,
  SubmissionModal,
} from 'pages/claims/components';

import { getParentClaim } from 'pages/claims/utils/claimTableRows';

import {
  useIsGeneratingSubmission,
  useIsProvider,
  useCanModify,
  useIsAdmin,
} from 'hooks';

interface ClaimToolBarProps {
  table: MRT_TableInstance<Claim>;
  payors: Payor[];
  providers: Provider[];
  handleCreateSubmission: (
    values: CreateSubmission,
    type: SubmissionType,
    email: string
  ) => Promise<Submission>;
  isFetching: boolean;
  refetchClaims: () => void;
  refetchStatusesCount: () => void;
  setCreateModalType: Dispatch<SetStateAction<string>>;
  setManualLoading: Dispatch<SetStateAction<boolean>>;
  setUseSelectedClaimForDuplicate: Dispatch<SetStateAction<boolean>>;
}

export const ClaimToolBar: React.FC<ClaimToolBarProps> = ({
  table,
  payors,
  providers,
  handleCreateSubmission,
  isFetching: isLoading,
  refetchClaims,
  refetchStatusesCount,
  setCreateModalType,
  setUseSelectedClaimForDuplicate,
  setManualLoading,
}) => {
  // Hooks
  const { id } = useCurrentSiteStore();
  const { email } = useCurrentUserStore();
  const { resetPagination } = useClaimPaginationStore();
  const { canModify } = useCanModify('claims');
  const isProvider = useIsProvider();
  const isAdmin = useIsAdmin();
  const { open: openNotification } = useNotification();
  const canOpenCreateModal = !!id && !isLoading && payors.length > 0;
  const canCreateClaim =
    !isProvider && !isAdmin && canModify && canOpenCreateModal;

  useHotkeys([
    [
      'mod+b',
      () => {
        if (!canCreateClaim) return;
        setUseSelectedClaimForDuplicate(false);
        setCreateModalType('claim');
        table.setCreatingRow(true);
      },
      { preventDefault: true },
    ],
    [
      'shift+mod+b',
      (event) => {
        event.preventDefault();
        if (!canCreateClaim) return;
        setUseSelectedClaimForDuplicate(false);
        setCreateModalType('bulk');
        table.setCreatingRow(true);
      },
    ],
    [
      'mod+i',
      (event) => {
        event.preventDefault();
        const selectedRow = table.getSelectedRowModel().rows[0]?.original;
        const selectedClaim = selectedRow ? getParentClaim(selectedRow) : null;
        if (selectedClaim) {
          const patientID = selectedClaim.patientID;
          setPatientID(patientID);
          setOpenPaymentHistory((prev) => !prev);
        } else {
          openNotification({
            message: 'Please select a row to view payment history',
            type: 'error',
          });
        }
      },
    ],
    [
      'F4',
      (event) => {
        event.preventDefault();
        const selectedRow = table.getSelectedRowModel().rows[0]?.original;
        const selectedClaim = selectedRow ? getParentClaim(selectedRow) : null;
        if (selectedClaim) {
          const patientID = selectedClaim.patientID;
          setPatientID(patientID);
        } else {
          setPatientID(undefined);
        }
        setOpenPatientHistory((prev) => !prev);
      },
    ],
  ]);

  const { isGenerating } = useIsGeneratingSubmission({
    siteID: id,
  });

  // Constants
  const selectedRow = table.getSelectedRowModel().rows[0]?.original;
  const selectedClaim = selectedRow ? getParentClaim(selectedRow) : null;
  const isBlueCross = selectedClaim?.payor?.name === 'Blue Cross';
  // State
  const [type, setType] = useState<SubmissionType>(null);
  const [payorType, setPayorType] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<
    [string | null, string | null]
  >([null, null]);
  const handleOpenModal = () => setOpenModal((prev) => !prev);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [patientID, setPatientID] = useState<string | undefined>(undefined);
  const [openPaymentHistory, setOpenPaymentHistory] = useState<boolean>(false);
  const [openPatientHistory, setOpenPatientHistory] = useState<boolean>(false);

  const handleSubmission = async (autoSubmit: boolean) => {
    handleOpenModal();
    const submissionData: CreateSubmission = {
      uploaded: false,
      status: 'Created',
      claims: [],
      siteID: id,
      payorName: payorType,
      type,
      autoSubmit,
    };
    switch (type) {
      case SubmissionType.date:
        submissionData.startDate =
          formatSelectedDateRange(selectedDateRange).startDate;
        submissionData.endDate =
          formatSelectedDateRange(selectedDateRange).endDate;
        break;
      case SubmissionType.provider:
        submissionData.providerID = selectedProvider;
        submissionData.startDate =
          formatSelectedDateRange(selectedDateRange).startDate;
        submissionData.endDate =
          formatSelectedDateRange(selectedDateRange).endDate;
        break;
    }
    await handleCreateSubmission(submissionData, type, email);
    resetPagination();
    refetchStatusesCount();
    refetchClaims();
  };

  const disableSubmitButton = (): boolean => {
    if (!payorType) return true;
    switch (type) {
      case SubmissionType.ready:
        return false;
      case SubmissionType.date:
        return !hasCompleteSelectedDateRange(selectedDateRange);
      case SubmissionType.provider:
        return (
          !selectedProvider ||
          (!hasEmptySelectedDateRange(selectedDateRange) &&
            !hasCompleteSelectedDateRange(selectedDateRange))
        );
    }
  };

  const handleResetHistoryModals = () => {
    table.setRowSelection({});
    setPatientID(undefined);
    table.refs.tableContainerRef.current?.blur();
  };

  const ToolbarButtons = (
    <>
      {!isProvider && (
        <>
          <TableActionButton
            leftIcon={<IconFilePlus />}
            onClick={handleOpenModal}
            disabled={isLoading || isGenerating || !canModify}
            dataTestId="generate-submission-button"
          >
            Generate Submission File
          </TableActionButton>
          <ClaimCreateComboButton
            table={table}
            isLoading={isLoading}
            setCreateModalType={setCreateModalType}
            disabled={!canCreateClaim}
          />
          <InternalUploadDataButton
            variant="create"
            disabled={isLoading || !canModify}
          />
          {isBlueCross && (
            <GenerateBlueCrossButton
              isLoading={isLoading}
              setIsLoading={setManualLoading}
              disabled={!selectedRow || !canModify}
              selectedRow={selectedRow}
            />
          )}
        </>
      )}
    </>
  );

  return (
    <Flex
      justify="flex-start"
      direction="column"
      gap="sm"
      className="claims-toolbar"
      style={{
        flexWrap: 'wrap',
      }}
      data-testid="claim-tool-bar"
    >
      <SubmissionModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        handleOpenModal={handleOpenModal}
        providers={providers}
        payors={payors}
        table={table as unknown as MRT_TableInstance<Claim>}
        submissionType={type}
        setSubmissionType={setType}
        payorType={payorType}
        setPayorType={setPayorType}
        handleSubmission={handleSubmission}
        disableSubmitButton={disableSubmitButton}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedProvider={selectedProvider}
        setSelectedProvider={setSelectedProvider}
      />
      <ClaimPatientPaymentHistoryModal
        patientID={patientID}
        healthcard={selectedClaim?.healthcard}
        open={openPaymentHistory}
        onClose={() => {
          handleResetHistoryModals();
          setOpenPaymentHistory(false);
        }}
        patient={selectedClaim?.patient}
        payors={payors}
      />
      <PatientHistoryModal
        open={openPatientHistory}
        onClose={() => {
          if (patientID) handleResetHistoryModals();
          setOpenPatientHistory(false);
        }}
        payors={payors}
        initialPatientID={patientID}
        initialPatient={selectedClaim?.patient}
        initialHealthcard={selectedClaim?.healthcard}
      />
      <Group className="claims-toolbar-group" gap="sm" align="center">
        {ToolbarButtons}
      </Group>
    </Flex>
  );
};
